/* ==========================================================================
   EVENA - theme.js
   All functions are idempotent: they can be called again
   after shopify:section:load without duplicating listeners.
   ========================================================================== */
(function () {
  'use strict';

  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- utils ---------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    format = format || (window.EvenaConfig && window.EvenaConfig.moneyFormat) || '{{ amount_with_comma_separator }} €';
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = thousands || ' ';
      decimal = decimal || ',';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }
    var match = format.match(placeholderRegex);
    var key = match ? match[1] : 'amount';
    switch (key) {
      case 'amount': value = formatWithDelimiters(cents, 2, ',', '.'); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0, ',', '.'); break;
      /* thousands with a dot: that is Shopify's Liquid rendering for this format,
         the JS must print exactly the same price as the page */
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default: value = formatWithDelimiters(cents, 2, ' ', ',');
    }
    /* strip any HTML format (<span class=money>...) then decode the
       entities (&nbsp; before the symbol in FR): the result works equally in
       textContent qu'en innerHTML */
    var out = format.replace(placeholderRegex, value).replace(/<[^>]+>/g, '');
    if (out.indexOf('&') !== -1) {
      var ta = document.createElement('textarea');
      ta.innerHTML = out;
      out = ta.value;
    }
    return out.trim();
  }

  /* ---------- reveal / wipe ---------- */
  var revealObserver = null;
  function reveal() {
    var targets = $$('[data-reveal], [data-reveal-stagger], [data-wipe]');
    if (DESIGN_MODE || REDUCED || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var list = (entry.target._evRevealList || []).slice();
          if (entry.target.dataset.evReveal && list.indexOf(entry.target) < 0) list.push(entry.target);
          /* the observer responded: the safety net is no longer needed */
          list.forEach(function (t) {
            if (t._evRevealT) { clearTimeout(t._evRevealT); t._evRevealT = null; }
          });
          if (entry.isIntersecting) {
            list.forEach(function (t) { t.classList.add('in'); });
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    }
    targets.forEach(function (el) {
      if (el.classList.contains('in') || el.dataset.evReveal) return;
      el.dataset.evReveal = '1';
      /* a [data-wipe] element starts fully clipped (clip-path), and Chromium
         counts that clip in the intersection: it never reports as visible.
         Observe its unclipped parent instead. */
      var watch = (el.hasAttribute('data-wipe') && el.parentElement) ? el.parentElement : el;
      if (watch !== el) {
        (watch._evRevealList = watch._evRevealList || []).push(el);
      }
      revealObserver.observe(watch);
      el._evRevealT = setTimeout(function () { el.classList.add('in'); }, 2600);
    });
  }

  /* ---------- scroll-fill (manifeste / citation) ---------- */
  var fillTicking = false;
  function scrollFillUpdate() {
    fillTicking = false;
    var vh = window.innerHeight;
    $$('[data-scrollfill]').forEach(function (host) {
      var words = $$('.fw', host);
      if (!words.length) return;
      var rect = host.getBoundingClientRect();
      var start = vh * 0.82;
      var end = vh * 0.32;
      var progress = (start - rect.top) / (start - end + rect.height);
      progress = Math.max(0, Math.min(1, progress));
      var count = Math.round(progress * words.length);
      words.forEach(function (w, i) { w.classList.toggle('on', i < count); });
    });
  }
  function scrollFill() {
    $$('[data-scrollfill]').forEach(function (host) {
      if (host.dataset.evFill) return;
      host.dataset.evFill = '1';
      var raw = host.textContent.trim();
      host.textContent = '';
      raw.split(/\s+/).forEach(function (word, i) {
        var span = document.createElement('span');
        span.className = 'fw';
        span.textContent = (i ? ' ' : '') + word;
        host.appendChild(span);
      });
      if (DESIGN_MODE || REDUCED) {
        $$('.fw', host).forEach(function (w) { w.classList.add('on'); });
      }
    });
    if (DESIGN_MODE || REDUCED) return;
    if (!document.documentElement.dataset.evFillScroll) {
      document.documentElement.dataset.evFillScroll = '1';
      window.addEventListener('scroll', function () {
        if (!fillTicking) { fillTicking = true; requestAnimationFrame(scrollFillUpdate); }
      }, { passive: true });
    }
    scrollFillUpdate();
  }

  /* ---------- header ---------- */
  function header() {
    var hd = $('[data-header]');
    if (!hd || hd.dataset.evInit) return;
    hd.dataset.evInit = '1';
    var onScroll = function () {
      hd.classList.toggle('hd--scrolled', window.scrollY > 12);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    /* sous-menus desktop : clic + survol */
    $$('.hd__item', hd).forEach(function (item) {
      var link = $('.hd__link', item);
      var sub = $('.hd__sub', item);
      if (!sub || !link) return;
      item.addEventListener('mouseenter', function () { item.classList.add('open'); link.setAttribute('aria-expanded', 'true'); });
      item.addEventListener('mouseleave', function () { item.classList.remove('open'); link.setAttribute('aria-expanded', 'false'); });
      link.addEventListener('focus', function () { item.classList.add('open'); link.setAttribute('aria-expanded', 'true'); });
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) { item.classList.remove('open'); link.setAttribute('aria-expanded', 'false'); }
      });
    });
  }

  /* ---------- tiroirs (menu, panier) ---------- */
  var lastDrawerTrigger = null;
  var activeDrawer = null;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  function openDrawer(id) {
    var drawer = document.getElementById(id);
    var overlay = $('[data-overlay]');
    if (!drawer) return;
    $$('.drawer.open').forEach(function (d) { if (d !== drawer) d.classList.remove('open'); });
    drawer.classList.add('open');
    drawer.removeAttribute('aria-hidden');
    drawer.setAttribute('aria-modal', 'true');
    if (!drawer.hasAttribute('role')) drawer.setAttribute('role', 'dialog');
    activeDrawer = drawer;
    if (overlay) { overlay.hidden = false; requestAnimationFrame(function () { overlay.classList.add('open'); }); }
    document.body.classList.add('drawer-lock');
    var focusable = drawer.querySelector(FOCUSABLE);
    if (focusable) focusable.focus({ preventScroll: true });
  }
  function closeDrawers() {
    var overlay = $('[data-overlay]');
    $$('.drawer.open').forEach(function (d) { d.classList.remove('open'); d.setAttribute('aria-hidden', 'true'); });
    activeDrawer = null;
    if (overlay) {
      overlay.classList.remove('open');
      setTimeout(function () { overlay.hidden = true; }, 350);
    }
    document.body.classList.remove('drawer-lock');
    if (lastDrawerTrigger) { lastDrawerTrigger.focus({ preventScroll: true }); lastDrawerTrigger = null; }
  }
  function drawers() {
    if (document.documentElement.dataset.evDrawers) return;
    document.documentElement.dataset.evDrawers = '1';
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        e.preventDefault();
        lastDrawerTrigger = opener;
        openDrawer(opener.getAttribute('data-drawer-open'));
        return;
      }
      var closer = e.target.closest('[data-drawer-close]');
      if (closer || e.target.closest('[data-overlay]')) {
        if (!closer || closer.tagName !== 'A') e.preventDefault();
        closeDrawers();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeDrawers(); return; }
      /* focus trap: Tab stays inside the open drawer (WCAG 2.4.3) */
      if (e.key !== 'Tab' || !activeDrawer) return;
      var items = $$(FOCUSABLE, activeDrawer).filter(function (el) { return el.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      var inside = activeDrawer.contains(document.activeElement);
      if (!inside) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ---------- drag souris sur scrollers ---------- */
  function dragScroll() {
    $$('.hscroll, .pdp__thumbs, [data-drag]').forEach(function (el) {
      if (el.dataset.evDrag) return;
      el.dataset.evDrag = '1';
      var startX = 0; var startScroll = 0; var dragging = false; var moved = false;
      el.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        dragging = true; moved = false;
        startX = e.clientX; startScroll = el.scrollLeft;
      });
      window.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) { moved = true; el.classList.add('drag'); }
        el.scrollLeft = startScroll - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!dragging) return;
        dragging = false;
        /* moved is reset after the drag-end click: a drag released
           outside the area no longer swallows the next legitimate click */
        setTimeout(function () { el.classList.remove('drag'); moved = false; }, 0);
      });
      el.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);
    });
  }

  /* ---------- quantite +/- ---------- */
  function qty() {
    if (document.documentElement.dataset.evQty) return;
    document.documentElement.dataset.evQty = '1';
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty-plus], [data-qty-minus]');
      if (!btn) return;
      e.preventDefault();
      var input = btn.parentElement.querySelector('input');
      if (!input) return;
      var val = parseInt(input.value, 10) || 1;
      var min = input.hasAttribute('min') ? Math.max(0, parseInt(input.min, 10) || 0) : 1;
      if (btn.hasAttribute('data-qty-plus')) val += 1; else val -= 1;
      if (val < min) val = min;
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- panier ajax + slide-cart ---------- */
  function cartRequest(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw j; });
      return r.json();
    });
  }

  /* All basket mutations go through a single queue: two actions
     rapprochees ne peuvent plus se croiser ni afficher un etat perime. */
  var cartChain = Promise.resolve();
  function queueCart(fn) {
    var run = function () { return fn(); };
    var p = cartChain.then(run, run);
    cartChain = p.then(function () {}, function () {});
    return p;
  }

  /* One silent retry for a network hiccup or a 429/5xx. A refusal from
     Shopify (422: stock limit...) is final and carries a description. */
  function cartRequestRetry(url, body) {
    return cartRequest(url, body).catch(function (err) {
      if (err && err.status === 422) throw err;
      return new Promise(function (res) { setTimeout(res, 700); })
        .then(function () { return cartRequest(url, body); });
    });
  }

  /* last basket received from the server, used for the instant preview */
  var lastCart = null;

  function cartAnnounce(msg) {
    var live = $('[data-cart-live]');
    if (live) live.textContent = msg;
  }
  var cartErrTimer = null;
  function cartError(msg) {
    var box = $('[data-cart-error]');
    if (!box) return;
    box.textContent = msg;
    box.hidden = false;
    clearTimeout(cartErrTimer);
    cartErrTimer = setTimeout(function () { box.hidden = true; }, 6000);
  }

  /* Compare-at prices by variant id, in pence. /cart.js leaves them out:
     they come from the drawer's server render, then from the cart-compare
     section, bundled with every add and change request (Section Rendering
     API), so a basket change costs no extra round trip. */
  var compareAt = {};
  var COMPARE_SECTION = 'cart-compare';
  function readCompare(html) {
    var m = html && /data-cart-compare>([\s\S]*?)<\/script>/.exec(html);
    if (!m) return;
    try {
      var map = JSON.parse(m[1]);
      Object.keys(map).forEach(function (k) { compareAt[k] = map[k]; });
    } catch (e) {}
  }
  function readCompareSections(res) {
    if (res && res.sections) readCompare(res.sections[COMPARE_SECTION]);
    return res;
  }
  (function () {
    var tag = document.querySelector('script[data-cart-compare]');
    if (tag) readCompare(tag.outerHTML);
  })();
  /* same as Liquid's money_without_trailing_zeros: £180, not £180.00 */
  function moneyShort(cents, fmt) {
    return formatMoney(cents, fmt).replace(/[.,]00(?!\d)/, '');
  }

  function renderCart(cart) {
    lastCart = cart;
    var root = $('[data-cart-root]');
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      if (cart.item_count > 0) el.removeAttribute('data-zero'); else el.setAttribute('data-zero', '');
    });
    $$('[data-drawer-open="CartDrawer"]').forEach(function (el) {
      el.setAttribute('aria-label', 'Basket, ' + cart.item_count + (cart.item_count > 1 ? ' items' : ' item'));
    });
    if (!root) return;
    var fmt = root.getAttribute('data-money-format') || null;
    var list = $('[data-cart-items]', root);
    var emptyBox = $('[data-cart-empty]', root);
    var fullBox = $('[data-cart-full]', root);
    if (emptyBox && fullBox) {
      emptyBox.hidden = cart.item_count > 0;
      fullBox.hidden = cart.item_count === 0;
    }
    var foot = $('[data-cart-foot]', root);
    if (foot) foot.hidden = cart.item_count === 0;
    /* the button is rendered disabled when the basket is empty: re-enable it on add */
    var checkoutBtn = root.querySelector('[name="checkout"]');
    if (checkoutBtn) checkoutBtn.disabled = cart.item_count === 0;
    if (list) {
      /* focus preserved: if a control in the list is active, we find it again
         by its line key after the re-render (WCAG 2.4.3) */
      var af = document.activeElement;
      var refocus = null;
      if (af && list.contains(af)) {
        refocus = {
          key: af.getAttribute('data-key'),
          sel: af.hasAttribute('data-qty-plus') ? '[data-qty-plus]'
            : af.hasAttribute('data-qty-minus') ? '[data-qty-minus]'
            : af.hasAttribute('data-line-qty') ? '[data-line-qty]'
            : af.hasAttribute('data-line-remove') ? '[data-line-remove]' : null
        };
      }
      list.innerHTML = cart.items.map(function (item) {
        var key = escapeHtml(item.key);
        var name = escapeHtml(item.product_title);
        var img = item.image
          ? '<img src="' + escapeHtml(item.image.replace(/(\.[a-z]{3,4})(\?|$)/, '_200x200$1$2')) + '" alt="" width="96" height="96" loading="lazy">'
          : '';
        /* name and finish: same split as cart-drawer.liquid */
        var full = item.product_title || '';
        var cut = full.indexOf(' - ');
        var tName = cut > 0 ? full.slice(0, cut) : full;
        var tDesc = cut > 0 ? full.slice(cut + 3) : '';
        var vTitle = item.product_has_only_default_variant ? '' : (item.variant_title || '');
        if (vTitle && vTitle !== 'Default Title') {
          if (!tDesc || tDesc.indexOf(' or ') > -1) tDesc = vTitle;
          else if (tDesc.toLowerCase().indexOf(vTitle.toLowerCase()) === -1) tDesc += ' · ' + vTitle;
        }
        var desc = tDesc ? '<p class="scart__idesc" title="' + escapeHtml(tDesc) + '">' + escapeHtml(tDesc) + '</p>' : '';
        /* before price: compare-at (Eleva Days) or price before a cart
           discount, whichever is higher; same rule as cart-drawer.liquid */
        var unitWas = Math.max(compareAt[item.variant_id] || 0, item.original_price);
        var lineWas = unitWas * item.quantity;
        var lineSave = lineWas - item.final_line_price;
        /* price on the right of the quantity, struck "before" price just
           above it; the saving itself is given once, next to the Total */
        var linePrice = '<p class="scart__lprice">' +
            (lineSave > 0 ? '<s class="price__was scart__lwas"><span class="visually-hidden">Was </span>' + formatMoney(lineWas, fmt) + '</s>' : '') +
            '<span class="scart__lnow">' + formatMoney(item.final_line_price, fmt) + '</span>' +
          '</p>';
        var url = escapeHtml(item.url);
        var qty = item.quantity;
        return '<div class="scart__item">' +
          '<a class="scart__thumb" href="' + url + '" tabindex="-1" aria-hidden="true">' + img + '</a>' +
          '<div class="scart__info">' +
            '<a class="scart__iname" href="' + url + '">' + escapeHtml(tName) + '</a>' +
            desc +
          '</div>' +
          '<div class="scart__side">' +
            linePrice +
            '<div class="qty qty--sm scart__qty">' +
              qtyFirstKey(qty, key, name) +
              '<input type="text" inputmode="numeric" pattern="[0-9]*" value="' + qty + '" data-line-qty data-key="' + key + '" aria-label="Quantity of ' + name + '">' +
              '<button type="button" data-qty-plus data-key="' + key + '" aria-label="Increase quantity of ' + name + '">' + ICON_PLUS + '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
      }).join('');
      if (refocus && refocus.sel) {
        var again = refocus.key
          ? list.querySelector(refocus.sel + '[data-key="' + refocus.key.replace(/"/g, '\\"') + '"]')
          : null;
        ((again || list.querySelector(refocus.sel)) || $('[data-cart-root] [name="checkout"]') || list).focus({ preventScroll: true });
      }
    }
    var totalEl = $('[data-cart-total]', root);
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price, fmt);
    /* everything the customer saves: compare-at prices and discounts */
    var wasTotal = cart.items.reduce(function (sum, item) {
      return sum + Math.max(compareAt[item.variant_id] || 0, item.original_price) * item.quantity;
    }, 0);
    paintSavings(root, wasTotal - cart.total_price, fmt);
    /* No countdown to a threshold: UK delivery is free on every order,
       with no minimum spend. The drawer gauge is always full. */
    upsellRails(cart);
  }

  /* ---------- basket suggestions: up to two rails chosen by the basket ----------
     dressing table in the basket -> seats, then mirrors (storage instead when
     the table already has a mirror); seat -> storage + mirrors; storage ->
     storage + mirrors; mirror -> storage + seats. Cards come from the
     <template data-upsell-pool> of snippets/cart-drawer.liquid, already sorted
     by price. Never a product or a model already in the basket, never a
     dressing table, one card per model, 6 cards per rail at most. */
  var ICON_MINUS = '<svg class="ic" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M5 12h14"/></svg>';
  var ICON_PLUS = '<svg class="ic" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14"/></svg>';
  var ICON_TRASH = '<svg class="ic" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3"/></svg>';
  /* first key of a basket line's stepper: a bin at quantity 1 (removes the
     line), a minus above 1; same rule as cart-drawer.liquid */
  function qtyFirstKey(qty, key, name) {
    return qty > 1
      ? '<button type="button" data-qty-minus data-key="' + key + '" aria-label="Decrease quantity of ' + name + '">' + ICON_MINUS + '</button>'
      : '<button type="button" class="scart__trash" data-line-remove data-key="' + key + '" aria-label="Remove ' + name + ' from basket">' + ICON_TRASH + '</button>';
  }
  /* "Your savings" row of the drawer foot: shown only when there is one */
  function paintSavings(root, cents, fmt) {
    var row = root && $('[data-cart-savings]', root);
    if (!row) return;
    var amt = $('[data-cart-savings-amt]', row);
    if (cents > 0) {
      row.hidden = false;
      if (amt) amt.textContent = '\u2212' + moneyShort(cents, fmt);
    } else {
      row.hidden = true;
    }
  }
  function upsellFamily(type) {
    var t = String(type || '').toLowerCase();
    if (/stool|chair|seat/.test(t)) return 'seats';
    if (/mirror/.test(t)) return 'mirrors';
    if (/organiser|organizer|storage/.test(t)) return 'storage';
    if (/dressing table|vanity|desk/.test(t)) return 'table';
    return '';
  }
  function upsellModel(title) {
    return String(title || '').split(' - ')[0].trim().toLowerCase();
  }
  function upsellRails(cart) {
    var root = $('[data-upsell-root]');
    if (!root) return;
    var items = (cart && cart.items) || [];
    var inCart = {}, models = {}, has = {}, tableWithMirror = false;
    items.forEach(function (it) {
      inCart[String(it.product_id)] = true;
      models[upsellModel(it.product_title)] = true;
      var fam = upsellFamily(it.product_type);
      if (fam) has[fam] = true;
      if (fam === 'table' && /\bmirror\b/i.test(it.product_title || '')) tableWithMirror = true;
    });
    var order = has.table ? ['seats', 'mirrors', 'storage']
      : has.seats ? ['storage', 'mirrors']
      : has.storage ? ['storage', 'mirrors']
      : has.mirrors ? ['storage', 'seats']
      : [];
    order = order.filter(function (fam) {
      if (fam === 'seats' && has.seats) return false;
      if (fam === 'mirrors' && (has.mirrors || tableWithMirror)) return false;
      return true;
    });
    var html = '';
    var sig = [];
    var shown = 0;
    order.forEach(function (fam) {
      if (shown >= 2) return;
      var tpl = document.querySelector('template[data-upsell-pool="' + fam + '"]');
      if (!tpl || !tpl.content) return;
      var cards = [];
      var taken = {};
      Array.prototype.forEach.call(tpl.content.querySelectorAll('.scd__card'), function (c) {
        if (cards.length >= 6) return;
        var pid = c.getAttribute('data-pid');
        var model = c.getAttribute('data-model') || pid;
        if (inCart[pid] || models[model] || taken[model]) return;
        taken[model] = true;
        cards.push(c.outerHTML);
        sig.push(pid);
      });
      if (cards.length < 2) return;
      shown++;
      sig.push(fam);
      var title = escapeHtml(tpl.getAttribute('data-title'));
      html += '<section class="scd__rail" aria-label="' + title + '">' +
        '<div class="scd__rhead">' +
          '<p class="scd__rtitle">' + title + '</p>' +
          '<a class="scd__rlink" href="' + escapeHtml(tpl.getAttribute('data-link')) + '">' + escapeHtml(tpl.getAttribute('data-link-text')) + '</a>' +
        '</div>' +
        '<div class="scd__track hscroll">' + cards.join('') + '</div>' +
      '</section>';
    });
    /* same suggestions as before (quantity change...): leave the rails as they
       are, so a customer scrolling a rail does not lose her place */
    var key = sig.join(',');
    if (root.getAttribute('data-sig') !== key) {
      root.innerHTML = html;
      root.setAttribute('data-sig', key);
      dragScroll();
    }
    root.hidden = !html;
  }

  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* a variant whose compare-at price is still unknown: ask the
           section once, then render (never a saving that pops in late) */
        var missing = cart.items.some(function (it) { return !(it.variant_id in compareAt); });
        if (!missing) return cart;
        return fetch('/?sections=' + COMPARE_SECTION, { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (sections) { readCompareSections({ sections: sections }); return cart; })
          .catch(function () { return cart; });
      })
      .then(function (cart) { renderCart(cart); return cart; });
  }

  function cartAjax() {
    if (document.documentElement.dataset.evCart) return;
    document.documentElement.dataset.evCart = '1';

    /* product form interception */
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form || form.hasAttribute('data-no-ajax')) return;
      e.preventDefault();
      /* busy lock: covers double click, double tap on the sticky
         bar (programmatic click) and the double Enter key press */
      if (form.dataset.busy) return;
      form.dataset.busy = '1';
      /* focus will return to the add button when the drawer closes */
      lastDrawerTrigger = form.querySelector('[type="submit"]') || lastDrawerTrigger;
      var errBox = form.querySelector('[data-form-error]');
      if (errBox) errBox.hidden = true;
      var btn = form.querySelector('[type="submit"]');
      if (btn) { btn.classList.add('btn--loading'); btn.setAttribute('aria-busy', 'true'); }
      var data = new FormData(form);
      data.append('sections', COMPARE_SECTION);
      var addFailed = false;
      queueCart(function () {
        return fetch('/cart/add.js', { method: 'POST', body: data, headers: { 'Accept': 'application/json' } })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (j) { addFailed = true; throw j; });
            return r.json();
          })
          .then(readCompareSections);
      })
        .then(function () {
          /* the add succeeded: a refresh failure must not
             afficher un faux message d'erreur ni empecher l'ouverture */
          return refreshCart().catch(function () {});
        })
        .then(function () {
          openDrawer('CartDrawer');
          cartAnnounce('Item added to basket');
        })
        .catch(function (err) {
          var msg = addFailed && err && err.description
            ? err.description
            : 'This item can’t be added right now. Check your connection and try again.';
          if (errBox) { errBox.hidden = false; errBox.textContent = msg; }
        })
        .then(function () {
          if (btn) { btn.classList.remove('btn--loading'); btn.removeAttribute('aria-busy'); }
          delete form.dataset.busy;
        });
    });

    /* boutons upsell / ajout rapide */
    document.addEventListener('click', function (e) {
      var add = e.target.closest('[data-add-id]');
      if (!add) return;
      e.preventDefault();
      if (add.disabled) return;
      lastDrawerTrigger = add;
      add.disabled = true;
      add.classList.add('btn--loading');
      queueCart(function () {
        return cartRequest('/cart/add.js', { id: parseInt(add.getAttribute('data-add-id'), 10), quantity: 1, sections: COMPARE_SECTION })
          .then(readCompareSections);
      })
        .then(function () { return refreshCart().catch(function () {}); })
        .then(function () { openDrawer('CartDrawer'); cartAnnounce('Item added to basket'); })
        .catch(function () {
          cartError('This item can’t be added right now.');
          refreshCart().catch(function () {});
        })
        .then(function () { add.disabled = false; add.classList.remove('btn--loading'); });
    });

    /* quantities and removal in the drawer: targeted by line key,
       immune to index shifts between two operations */
    /* Rapid +/+/+ clicks: the number, line price, saving and total change
       at once, and a single request leaves 350 ms after the last click. The
       answer to an older request never redraws the list over newer clicks:
       the list is only redrawn once every line has caught up. */
    var qtyWant = {};   /* line key -> quantity asked for, not yet confirmed */
    var qtyTimer = {};  /* line key -> pending debounce */
    var qtyBusy = 0;    /* quantity requests in flight */
    function qtySettled() { return qtyBusy === 0 && Object.keys(qtyTimer).length === 0; }
    function qtyPreview(input, key, q) {
      var item = lastCart && lastCart.items.find(function (it) { return it.key === key; });
      if (!item) return;
      var root = $('[data-cart-root]');
      var fmt = (root && root.getAttribute('data-money-format')) || null;
      var line = input.closest('.scart__item');
      var price = line && line.querySelector('.scart__lnow');
      if (price) price.textContent = formatMoney(item.final_price * q, fmt);
      /* same "before" price rule as renderCart */
      var unitWas = Math.max(compareAt[item.variant_id] || 0, item.original_price);
      var was = line && line.querySelector('.scart__lwas');
      if (was) was.innerHTML = '<span class="visually-hidden">Was </span>' + formatMoney(unitWas * q, fmt);
      /* bin at 1, minus above 1, without waiting for the server */
      var first = line && line.querySelector('.scart__qty > button:first-child');
      if (first && (q > 1) !== first.hasAttribute('data-qty-minus')) {
        var hadFocus = document.activeElement === first;
        first.outerHTML = qtyFirstKey(q, escapeHtml(key), escapeHtml(item.product_title));
        if (hadFocus) { var nf = line.querySelector('.scart__qty > button:first-child'); if (nf) nf.focus({ preventScroll: true }); }
      }
      var total = lastCart.total_price;
      var count = lastCart.item_count;
      var wasTotal = 0;
      lastCart.items.forEach(function (it) {
        var n = qtyWant[it.key] != null ? qtyWant[it.key] : it.quantity;
        total += (n - it.quantity) * it.final_price;
        count += n - it.quantity;
        wasTotal += Math.max(compareAt[it.variant_id] || 0, it.original_price) * n;
      });
      var totalEl = root && $('[data-cart-total]', root);
      if (totalEl) totalEl.textContent = formatMoney(total, fmt);
      paintSavings(root, wasTotal - total, fmt);
      $$('[data-cart-count]').forEach(function (el) { el.textContent = count; });
    }
    function qtySend(key) {
      delete qtyTimer[key];
      var q = qtyWant[key];
      qtyBusy++;
      queueCart(function () {
        return cartRequestRetry('/cart/change.js', { id: key, quantity: q, sections: COMPARE_SECTION }).then(readCompareSections);
      })
        .then(function (cart) {
          qtyBusy--;
          if (qtyWant[key] === q) delete qtyWant[key];
          lastCart = cart;
          if (qtySettled()) { renderCart(cart); cartAnnounce('Quantity updated'); }
        }, function (err) {
          qtyBusy--;
          delete qtyWant[key];
          cartError((err && err.description) || 'The quantity could not be updated.');
          if (qtySettled()) refreshCart().catch(function () {});
        });
    }
    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-line-qty]');
      if (!input) return;
      var key = input.getAttribute('data-key');
      var q = parseInt(input.value, 10);
      if (isNaN(q) || q < 1) {
        /* champ vide ou saisie invalide : on resynchronise, on ne supprime pas.
           Removal is reserved for the Remove button. */
        if (qtySettled()) refreshCart().catch(function () {});
        return;
      }
      qtyWant[key] = q;
      if (lastCart) {
        qtyPreview(input, key, q);
      } else {
        /* drawer rendered by Liquid: fetch the basket once for the preview */
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (!lastCart) lastCart = cart;
            if (qtyWant[key] != null) qtyPreview(input, key, qtyWant[key]);
          })
          .catch(function () {});
      }
      clearTimeout(qtyTimer[key]);
      qtyTimer[key] = setTimeout(function () { qtySend(key); }, 350);
    });
    document.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-line-remove]');
      if (!rm) return;
      e.preventDefault();
      var key = rm.getAttribute('data-key');
      /* a quantity still waiting for this line is dropped with it */
      clearTimeout(qtyTimer[key]);
      delete qtyTimer[key];
      delete qtyWant[key];
      queueCart(function () { return cartRequest('/cart/change.js', { id: key, quantity: 0, sections: COMPARE_SECTION }).then(readCompareSections); })
        .then(function (cart) {
          lastCart = cart;
          if (qtySettled()) renderCart(cart);
          cartAnnounce('Item removed from basket');
        })
        .catch(function () {
          cartError('The item could not be removed.');
          refreshCart().catch(function () {});
        });
    });

    /* checkout : pas de double navigation */
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action="/cart"]');
      if (!form) return;
      var btn = form.querySelector('[name="checkout"]');
      if (!btn) return;
      if (btn.dataset.busy) { e.preventDefault(); return; }
      btn.dataset.busy = '1';
      btn.classList.add('btn--loading');
      setTimeout(function () { delete btn.dataset.busy; btn.classList.remove('btn--loading'); }, 6000);
    });
  }

  /* ---------- galerie PDP ---------- */
  function gallery() {
    $$('[data-gallery]').forEach(function (g) {
      if (g.dataset.evGallery) return;
      g.dataset.evGallery = '1';
      var slides = $$('.pdp__slide', g);
      var thumbs = $$('.pdp__thumb', g);
      if (!slides.length) return;
      var current = Math.max(0, slides.findIndex(function (s) { return s.classList.contains('cur'); }));
      function goTo(index) {
        if (index === current || !slides[index]) return;
        var dir = index > current ? 'from-r' : 'from-l';
        slides.forEach(function (s) { s.classList.remove('cur', 'from-r', 'from-l'); });
        slides[index].classList.add(dir);
        void slides[index].offsetWidth;
        slides[index].classList.add('cur');
        thumbs.forEach(function (t, i) { t.classList.toggle('cur', i === index); });
        if (thumbs[index]) thumbs[index].scrollIntoView({ block: 'nearest', inline: 'center', behavior: REDUCED ? 'auto' : 'smooth' });
        current = index;
      }
      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function (e) { e.preventDefault(); goTo(i); });
      });
      g.goToMedia = function (mediaId) {
        var index = slides.findIndex(function (s) { return s.getAttribute('data-media-id') === String(mediaId); });
        if (index >= 0) goTo(index);
      };
    });
  }

  /* ---------- fleches des scrollers horizontaux ---------- */
  function scrollNav() {
    if (document.documentElement.dataset.evSnav) return;
    document.documentElement.dataset.evSnav = '1';
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-scroll-prev], [data-scroll-next]');
      if (!btn) return;
      var host = btn.closest('section') || document;
      var zone = host.querySelector('[data-scroll-zone]');
      if (!zone) return;
      var step = Math.max(zone.clientWidth * 0.8, 280);
      var dir = btn.hasAttribute('data-scroll-prev') ? -1 : 1;
      zone.scrollBy({ left: dir * step, behavior: REDUCED ? 'auto' : 'smooth' });
    });
  }

  /* ---------- delivery details (PDP) ---------- */
  /* No calendar-date estimate: the product page states a lead time in
     working days, which never goes stale in the CDN cache. */
  function deliveryEstimate() {}

  function deliveryDetail() {
    if (document.documentElement.dataset.evDelivery) return;
    document.documentElement.dataset.evDelivery = '1';
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-delivery-more]');
      if (!btn) return;
      e.preventDefault();
      var panel = $('[data-delivery-panel]');
      if (!panel) return;
      var open = panel.hidden;
      panel.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
    });
  }

  /* ---------- barre d'achat sticky (PDP mobile) ---------- */
  function stickyAtc() {
    var bar = $('[data-sticky-atc]');
    var addBtn = $('[data-add-btn]');
    if (!bar || !addBtn || bar.dataset.evInit) return;
    bar.dataset.evInit = '1';
    if (!('IntersectionObserver' in window)) return;
    var pastBtn = false;
    var nearFoot = false;
    function apply() {
      var on = pastBtn && !nearFoot;
      if (on) {
        /* reflow between hidden=false and .on to let the transition play */
        if (bar.hidden) { bar.hidden = false; void bar.offsetHeight; }
        bar.classList.add('on');
      } else {
        bar.classList.remove('on');
        setTimeout(function () { if (!bar.classList.contains('on')) bar.hidden = true; }, 380);
      }
    }
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      pastBtn = !e.isIntersecting && e.boundingClientRect.top < 0;
      apply();
    }, { threshold: 0 });
    io.observe(addBtn);
    var foot = document.querySelector('footer.ft');
    if (foot) {
      var ioFoot = new IntersectionObserver(function (entries) {
        nearFoot = entries[0].isIntersecting;
        apply();
      }, { threshold: 0 });
      ioFoot.observe(foot);
    }
    var trigger = $('[data-sticky-add]', bar);
    if (trigger) {
      trigger.addEventListener('click', function () {
        if (!addBtn.disabled) addBtn.click();
      });
    }
  }

  /* ---------- formulaire produit (variantes) ---------- */
  function productForm() {
    $$('[data-product-form]').forEach(function (root) {
      if (root.dataset.evPform) return;
      root.dataset.evPform = '1';
      var jsonTag = $('[data-product-json]', root);
      if (!jsonTag) return;
      var product;
      try { product = JSON.parse(jsonTag.textContent); } catch (err) { return; }
      var mediaMapTag = $('[data-variant-media]', root);
      var mediaMap = {};
      if (mediaMapTag) { try { mediaMap = JSON.parse(mediaMapTag.textContent); } catch (err) {} }
      var idInput = $('[data-variant-id]', root);
      var fmt = root.getAttribute('data-money-format') || null;

      function selectedOptions() {
        var opts = [];
        $$('[data-option-index]', root).forEach(function (el) {
          var index = parseInt(el.getAttribute('data-option-index'), 10);
          if (el.tagName === 'SELECT') opts[index] = el.value;
          else if (el.checked) opts[index] = el.value;
        });
        return opts;
      }
      function findVariant(opts) {
        return product.variants.find(function (v) {
          return v.options.every(function (o, i) { return o === opts[i]; });
        });
      }
      /* several options (colour x legs...): not every pair exists. A value that
         cannot be bought with the current choice of the other options is dimmed,
         and picking it moves the other options to a combination that exists,
         so the customer never lands on a dead "Unavailable" button. */
      function setOption(index, value) {
        $$('[data-option-index="' + index + '"]', root).forEach(function (el) {
          if (el.tagName === 'SELECT') el.value = value;
          else el.checked = (el.value === value);
          if (el.tagName === 'SELECT' || el.checked) {
            var set = el.closest('.optset');
            var val = set && set.querySelector('.optset__val');
            if (val) val.textContent = value;
          }
        });
      }
      function markCombos() {
        if (!product.options || product.options.length < 2) return;
        var opts = selectedOptions();
        $$('input[data-option-index]', root).forEach(function (el) {
          var index = parseInt(el.getAttribute('data-option-index'), 10);
          var test = opts.slice();
          test[index] = el.value;
          var ok = product.variants.some(function (v) {
            return v.available && v.options.every(function (o, i) { return o === test[i]; });
          });
          var label = el.id ? root.querySelector('label[for="' + el.id + '"]') : null;
          if (label) label.classList.toggle('is-na', !ok);
        });
      }
      function update() {
        var variant = findVariant(selectedOptions());
        var btn = $('[data-add-btn]', root);
        var priceNow = $('[data-price-now]', root);
        var priceWas = $('[data-price-was]', root);
        var priceBadge = $('[data-price-badge]', root);
        var priceSave = $('[data-price-save]', root);
        var payPer = $$('[data-pay-per]', document);
        if (!variant) {
          if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.disabled = true; $('[data-add-label]', btn).textContent = 'Unavailable'; }
          markCombos();
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (btn) {
          btn.disabled = !variant.available;
          if (variant.available) { btn.removeAttribute('aria-disabled'); $('[data-add-label]', btn).textContent = btn.getAttribute('data-label-add'); }
          else { btn.setAttribute('aria-disabled', 'true'); $('[data-add-label]', btn).textContent = 'Sold out'; }
        }
        if (priceNow) priceNow.textContent = formatMoney(variant.price, fmt);
        if (priceWas) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceWas.hidden = false;
            priceWas.textContent = formatMoney(variant.compare_at_price, fmt);
            if (priceBadge) {
              priceBadge.hidden = false;
              priceBadge.textContent = '-' + Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100) + '%';
            }
            if (priceSave) {
              priceSave.hidden = false;
              /* same as Liquid's money_without_trailing_zeros: £30, not £30.00 */
              priceSave.textContent = 'You save ' + formatMoney(variant.compare_at_price - variant.price, fmt).replace(/[.,]00(?!\d)/, '');
            }
          } else {
            priceWas.hidden = true;
            if (priceBadge) priceBadge.hidden = true;
            if (priceSave) priceSave.hidden = true;
          }
        }
        payPer.forEach(function (el) {
          var n = parseInt(el.getAttribute('data-n'), 10) || 3;
          el.textContent = formatMoney(Math.round(variant.price / n), fmt);
        });
        /* sync of the sticky bar (outside the form container) */
        var stickyBtn = document.querySelector('[data-sticky-add]');
        if (stickyBtn && btn) stickyBtn.disabled = btn.disabled;
        var stickyPrice = document.querySelector('[data-sticky-atc] [data-price-now]');
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price, fmt);
        if (mediaMap[variant.id]) {
          var g = $('[data-gallery]');
          if (g && g.goToMedia) g.goToMedia(mediaMap[variant.id]);
        }
        if (history.replaceState && root.getAttribute('data-url')) {
          history.replaceState({}, '', root.getAttribute('data-url') + '?variant=' + variant.id);
        }
        markCombos();
      }
      root.addEventListener('change', function (e) {
        var opt = e.target.closest('[data-option-index]');
        if (!opt) return;
        var set = opt.closest('.optset');
        if (set) {
          var val = set.querySelector('.optset__val');
          if (val) val.textContent = opt.value;
        }
        if (!findVariant(selectedOptions())) {
          var index = parseInt(opt.getAttribute('data-option-index'), 10);
          var near = product.variants.filter(function (v) { return v.options[index] === opt.value; });
          var pick = near.find(function (v) { return v.available; }) || near[0];
          if (pick) pick.options.forEach(function (o, i) { if (i !== index) setOption(i, o); });
        }
        update();
      });
      markCombos();
    });
  }

  /* ---------- init ---------- */
  function initAll() {
    reveal();
    scrollFill();
    header();
    drawers();
    dragScroll();
    qty();
    cartAjax();
    gallery();
    productForm();
    stickyAtc();
    deliveryDetail();
    deliveryEstimate();
    scrollNav();
    refreshCart().catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* fix editeur : re-init apres chargement/selection de section */
  if (DESIGN_MODE) {
    document.addEventListener('shopify:section:load', function () {
      reveal(); scrollFill(); header(); dragScroll(); gallery(); productForm(); stickyAtc(); deliveryDetail(); deliveryEstimate();
    });
    document.addEventListener('shopify:section:select', function () { reveal(); });
    document.addEventListener('shopify:block:select', function () { reveal(); });
  }

  window.EvenaTheme = {
    openDrawer: openDrawer,
    closeDrawers: closeDrawers,
    refreshCart: refreshCart,
    formatMoney: formatMoney,
    reinit: initAll
  };
})();
